import React, { useState, useRef } from "react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend,
} from "recharts";
import { Plus, Search, Eye, GitBranch, CheckCircle2, ArrowRightCircle, FlaskConical, Clock, AlertTriangle, Trash2, Upload } from "lucide-react";
import { api } from "@/lib/api";
import { useList } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader, Loading, EmptyState, StatusBadge, fmt } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Lightbox, Thumb } from "@/components/Lightbox";

const SAMPLE_STATUSES = ["Requested", "Pending Development", "In Development", "Pattern Making", "Cutting", "Printing", "Embroidery", "Stitching", "Finishing", "QC Pending", "Internal Review", "Sent for Approval", "Revision Required", "Approved", "Rejected", "On Hold", "Completed"];
const APPROVAL_STATUSES = ["Pending", "Internally Approved", "Sent to Buyer", "Buyer Approved", "Buyer Rejected", "Revision Required", "Final Approved"];
const CHART = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

function KpiMini({ icon: Icon, label, value, tone }) {
  const t = { success: "text-success", warning: "text-warning", danger: "text-destructive", primary: "text-primary" }[tone] || "text-foreground";
  return (
    <Card className="kpi-card p-4">
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</span>
        <Icon className={`w-4 h-4 ${t}`} />
      </div>
      <div className={`font-num font-bold text-2xl mt-1 ${t}`}>{value}</div>
    </Card>
  );
}

function SampleForm({ open, onClose, products, customers, sampleTypes, printingTypes }) {
  const [f, setF] = useState({
    buyer: "", brand: "", product_name: "", sku: "", sample_type: "", fabric_type: "", fabric_gsm: "",
    fabric_color: "", size: "M", measurement_details: "", printing_type: "", special_instructions: "",
    assigned_employee: "", sample_quantity: 1, sample_start_date: "", required_completion_date: "",
    status: "Requested", approval_status: "Pending",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const qc = useQueryClient();
  const submit = async () => {
    if (!f.product_name || !f.buyer) return toast.error("Buyer and product required");
    try { await api.post("/samples", f); toast.success("Sample created"); qc.invalidateQueries(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sample-form">
        <DialogHeader><DialogTitle>New Sample Request</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Buyer</Label>
            <Select value={f.buyer} onValueChange={(v) => set("buyer", v)}>
              <SelectTrigger data-testid="sample-buyer-select"><SelectValue placeholder="Buyer" /></SelectTrigger>
              <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Product</Label>
            <Select value={f.product_name} onValueChange={(v) => { const p = products.find((x) => x.name === v); set("product_name", v); if (p) { set("sku", p.sku); set("fabric_type", p.fabric); set("fabric_gsm", p.gsm); set("fabric_color", p.default_color); } }}>
              <SelectTrigger data-testid="sample-product-select"><SelectValue placeholder="Product" /></SelectTrigger>
              <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Brand</Label><Input value={f.brand} onChange={(e) => set("brand", e.target.value)} /></div>
          <div><Label>Sample Type</Label>
            <Select value={f.sample_type} onValueChange={(v) => set("sample_type", v)}>
              <SelectTrigger data-testid="sample-type-select"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>{sampleTypes.map((t) => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Fabric</Label><Input value={f.fabric_type} onChange={(e) => set("fabric_type", e.target.value)} /></div>
          <div><Label>Fabric Color</Label><Input value={f.fabric_color} onChange={(e) => set("fabric_color", e.target.value)} /></div>
          <div><Label>Printing Type</Label>
            <Select value={f.printing_type} onValueChange={(v) => set("printing_type", v)}>
              <SelectTrigger><SelectValue placeholder="Printing" /></SelectTrigger>
              <SelectContent>{printingTypes.map((t) => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Assigned Employee</Label><Input value={f.assigned_employee} onChange={(e) => set("assigned_employee", e.target.value)} /></div>
          <div><Label>Start Date</Label><Input type="date" value={f.sample_start_date} onChange={(e) => set("sample_start_date", e.target.value)} /></div>
          <div><Label>Required Date</Label><Input type="date" value={f.required_completion_date} onChange={(e) => set("required_completion_date", e.target.value)} data-testid="sample-required-date" /></div>
        </div>
        <div><Label>Measurement Details</Label><Textarea value={f.measurement_details} onChange={(e) => set("measurement_details", e.target.value)} rows={2} /></div>
        <div><Label>Special Instructions</Label><Textarea value={f.special_instructions} onChange={(e) => set("special_instructions", e.target.value)} rows={2} /></div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} data-testid="sample-save-btn">Create Sample</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SampleDetail({ id, onClose }) {
  const { canWrite } = useAuth();
  const qc = useQueryClient();
  const { data: s, isLoading } = useList(`sample-${id}`, `/samples/${id}`);
  const [status, setStatus] = useState("");
  const [approval, setApproval] = useState("");
  const [comment, setComment] = useState("");
  const [override, setOverride] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [lb, setLb] = useState(null);
  const photoRef = useRef(null);
  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    const urls = [];
    for (const file of files) { const fd = new FormData(); fd.append("file", file); try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); urls.push(`${process.env.REACT_APP_BACKEND_URL}${data.url}`); } catch { toast.error(`Failed: ${file.name}`); } }
    if (urls.length) { try { await api.post(`/samples/${id}/images`, { images: urls }); toast.success("Photos added"); qc.invalidateQueries(); } catch { toast.error("Failed to attach"); } }
    if (e.target) e.target.value = "";
  };
  const [conv, setConv] = useState({ order_quantity: "", delivery_date: "", production_start_date: "", daily_target: "", priority: "Medium" });

  const saveStatus = async () => { try { await api.put(`/samples/${id}`, { status }); toast.success("Status updated"); qc.invalidateQueries(); } catch { toast.error("Failed"); } };
  const saveApproval = async () => {
    if (!approval) return toast.error("Choose an approval status");
    try { await api.post(`/samples/${id}/approval`, { approval_status: approval, comments: comment }); toast.success("Approval recorded"); qc.invalidateQueries(); setComment(""); }
    catch { toast.error("Failed"); }
  };
  const addRevision = async () => {
    const reason = window.prompt("Revision reason / notes:");
    if (reason === null) return;
    try { await api.post(`/samples/${id}/revision`, { reason }); toast.success("Revision created"); qc.invalidateQueries(); } catch { toast.error("Failed"); }
  };
  const convert = async () => {
    try {
      await api.post(`/samples/${id}/convert`, { ...conv, order_quantity: Number(conv.order_quantity) || 0, daily_target: Number(conv.daily_target) || 0, override });
      toast.success("Bulk production order created"); qc.invalidateQueries(); setConvertOpen(false); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Convert failed"); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="sample-detail">
        {isLoading || !s ? <Loading /> : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3 flex-wrap">
                <span className="font-num text-primary">{s.sample_id}</span>
                <span className="text-base font-normal">{s.product_name}</span>
                <span className="text-xs px-2 py-0.5 rounded bg-secondary font-semibold">V{s.revision_number}</span>
                <StatusBadge status={s.status} />
                <StatusBadge status={s.approval_status} />
              </DialogTitle>
            </DialogHeader>

            <div className="grid sm:grid-cols-3 gap-3 text-sm">
              <Info label="Buyer" value={s.buyer} /><Info label="Brand" value={s.brand} /><Info label="Sample Type" value={s.sample_type} />
              <Info label="Fabric" value={`${s.fabric_type || ""} ${s.fabric_gsm ? s.fabric_gsm + " GSM" : ""}`} /><Info label="Color" value={s.fabric_color} /><Info label="Printing" value={s.printing_type} />
              <Info label="Assigned" value={s.assigned_employee} /><Info label="Required" value={s.required_completion_date} /><Info label="Total Cost" value={s.total_sample_cost ? `₹${s.total_sample_cost}` : "—"} />
            </div>
            {s.measurement_details && <div className="text-sm"><span className="text-muted-foreground">Measurements: </span>{s.measurement_details}</div>}
            {s.special_instructions && <div className="text-sm"><span className="text-muted-foreground">Instructions: </span>{s.special_instructions}</div>}

            <div className="border-t border-border pt-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Sample Photos</h4>
                {canWrite("samples") && <><input ref={photoRef} type="file" accept="image/*" multiple className="hidden" onChange={addPhotos} /><Button size="sm" variant="outline" className="gap-1" onClick={() => photoRef.current?.click()} data-testid="sample-add-photos"><Upload className="w-3.5 h-3.5" /> Add Photos</Button></>}
              </div>
              {(s.images || []).length > 0 ? (
                <div className="flex flex-wrap gap-2" data-testid="sample-photo-gallery">
                  {s.images.map((im, idx) => <Thumb key={idx} src={im} size="w-16 h-16" testid={`sample-photo-${idx}`} onClick={() => setLb(idx)} />)}
                </div>
              ) : <p className="text-xs text-muted-foreground">No photos yet.</p>}
            </div>
            {lb !== null && <Lightbox images={s.images || []} index={lb} title={`${s.sample_id} · ${s.product_name}`} subtitle={[s.sample_type, s.fabric_type, s.fabric_color].filter(Boolean).join(" · ")} onClose={() => setLb(null)} />}

            {canWrite("samples") && (
              <div className="grid sm:grid-cols-2 gap-4 border-t border-border pt-4">
                <div>
                  <Label>Update Workflow Stage</Label>
                  <div className="flex gap-2 mt-1">
                    <Select value={status} onValueChange={setStatus}>
                      <SelectTrigger data-testid="sample-status-select"><SelectValue placeholder={s.status} /></SelectTrigger>
                      <SelectContent>{SAMPLE_STATUSES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button onClick={saveStatus} data-testid="sample-status-save">Set</Button>
                  </div>
                  <Button variant="outline" size="sm" className="mt-2 gap-1" onClick={addRevision} data-testid="sample-revision-btn"><GitBranch className="w-3.5 h-3.5" /> New Revision</Button>
                </div>
                <div>
                  <Label>Approval Action</Label>
                  <div className="flex gap-2 mt-1">
                    <Select value={approval} onValueChange={setApproval}>
                      <SelectTrigger data-testid="sample-approval-select"><SelectValue placeholder="Approval status" /></SelectTrigger>
                      <SelectContent>{APPROVAL_STATUSES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button onClick={saveApproval} data-testid="sample-approval-save">Apply</Button>
                  </div>
                  <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Approval comment / buyer feedback" className="mt-2 text-sm" />
                </div>
              </div>
            )}

            {/* Convert to bulk */}
            <div className="border-t border-border pt-4">
              {s.bulk_order_id ? (
                <div className="text-sm text-success font-semibold flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Converted to bulk order {s.bulk_order_id}</div>
              ) : canWrite("orders") ? (
                <Button onClick={() => setConvertOpen(true)} className="gap-2" variant={s.approval_status === "Final Approved" ? "default" : "outline"} data-testid="convert-bulk-btn">
                  <ArrowRightCircle className="w-4 h-4" /> Create Bulk Production Order
                </Button>
              ) : null}
              {s.approval_status !== "Final Approved" && !s.bulk_order_id && (
                <p className="text-xs text-warning mt-2 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Sample not Final Approved — conversion needs Admin/Manager override.</p>
              )}
            </div>

            {/* Revision history */}
            {s.revisions?.length > 0 && (
              <div className="border-t border-border pt-4">
                <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-2">Revision History</h4>
                <div className="space-y-1.5">
                  {s.revisions.map((r) => (
                    <div key={r.id} className="text-xs flex justify-between p-2 rounded bg-secondary/40">
                      <span className="font-semibold">V{r.revision_number}</span>
                      <span className="text-muted-foreground flex-1 px-2 truncate">{r.reason || "—"}</span>
                      <span className="text-muted-foreground">{r.created_by}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>

      {convertOpen && (
        <Dialog open onOpenChange={() => setConvertOpen(false)}>
          <DialogContent data-testid="convert-form">
            <DialogHeader><DialogTitle>Bulk Order Details</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">Approved sample specs will be copied automatically. Enter bulk parameters:</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Order Quantity</Label><Input type="number" value={conv.order_quantity} onChange={(e) => setConv({ ...conv, order_quantity: e.target.value })} data-testid="convert-qty" /></div>
              <div><Label>Daily Target</Label><Input type="number" value={conv.daily_target} onChange={(e) => setConv({ ...conv, daily_target: e.target.value })} /></div>
              <div><Label>Delivery Date</Label><Input type="date" value={conv.delivery_date} onChange={(e) => setConv({ ...conv, delivery_date: e.target.value })} /></div>
              <div><Label>Start Date</Label><Input type="date" value={conv.production_start_date} onChange={(e) => setConv({ ...conv, production_start_date: e.target.value })} /></div>
              <div className="col-span-2"><Label>Priority</Label>
                <Select value={conv.priority} onValueChange={(v) => setConv({ ...conv, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["Low", "Medium", "High", "Urgent"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={override} onCheckedChange={setOverride} data-testid="convert-override" /> Override if not Final Approved (Admin/Manager)</label>
            <DialogFooter><Button variant="outline" onClick={() => setConvertOpen(false)}>Cancel</Button><Button onClick={convert} data-testid="convert-submit">Create Order</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}

function Info({ label, value }) {
  return <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div><div className="font-medium">{value || "—"}</div></div>;
}

export default function Sampling() {
  const { canWrite, user } = useAuth();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const { data: dash, isLoading } = useList("sampling-dash", "/sampling/dashboard");
  const { data: products = [] } = useList("products", "/products");
  const { data: customers = [] } = useList("customers", "/customers");
  const { data: sampleTypes = [] } = useList("sample_types", "/sample_types");
  const { data: printingTypes = [] } = useList("printing_types", "/printing_types");

  const delSample = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm("Delete this sample and its revision/approval history? This cannot be undone.")) return;
    try { await api.delete(`/samples/${id}`); toast.success("Sample deleted"); qc.invalidateQueries(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };
  const clearAll = async () => {
    const all = dash?.samples || [];
    if (!all.length) return;
    if (!window.confirm(`Delete ALL ${all.length} samples? This removes every sample and its history. This cannot be undone.`)) return;
    try {
      toast.loading("Clearing samples…", { id: "clr" });
      for (const s of all) await api.delete(`/samples/${s.id}`);
      toast.success("All samples cleared", { id: "clr" });
      qc.invalidateQueries();
    } catch (err) { toast.error("Failed to clear", { id: "clr" }); }
  };

  const k = dash?.kpis;
  const samples = (dash?.samples || []).filter((s) => !search || (s.sample_id + s.product_name + s.buyer).toLowerCase().includes(search.toLowerCase()));
  const pie = k ? [
    { name: "In Dev", value: k.in_development }, { name: "Awaiting", value: k.awaiting_approval },
    { name: "Revision", value: k.requiring_revision }, { name: "Approved", value: k.approved }, { name: "Rejected", value: k.rejected },
  ].filter((x) => x.value > 0) : [];

  if (isLoading) return <Loading />;

  return (
    <div>
      <PageHeader title="Sampling Department" subtitle="Pre-production development, revisions & buyer approvals" testid="sampling-header">
        {canWrite("samples") && <Button onClick={() => setNewOpen(true)} className="gap-2" data-testid="new-sample-btn"><Plus className="w-4 h-4" /> New Sample</Button>}
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 mb-4">
        <KpiMini icon={FlaskConical} label="Active Samples" value={fmt(k?.total_active)} tone="primary" />
        <KpiMini icon={Plus} label="Requested Today" value={fmt(k?.requested_today)} />
        <KpiMini icon={Clock} label="In Development" value={fmt(k?.in_development)} tone="primary" />
        <KpiMini icon={Eye} label="Awaiting Approval" value={fmt(k?.awaiting_approval)} tone="warning" />
        <KpiMini icon={GitBranch} label="Under Revision" value={fmt(k?.requiring_revision)} tone="warning" />
        <KpiMini icon={CheckCircle2} label="Approved" value={fmt(k?.approved)} tone="success" />
        <KpiMini icon={AlertTriangle} label="Rejected" value={fmt(k?.rejected)} tone="danger" />
        <KpiMini icon={AlertTriangle} label="Overdue" value={fmt(k?.overdue)} tone="danger" />
        <KpiMini icon={CheckCircle2} label="Approval Rate" value={`${fmt(k?.approval_rate)}%`} tone="success" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 overflow-hidden">
          <div className="p-3 border-b border-border relative">
            <Search className="w-4 h-4 absolute left-6 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search samples…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="sampling-search" />
          </div>
          {!samples.length ? <EmptyState title="No samples" hint="Create a sample request to start." /> : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Sample</TableHead><TableHead>Customer</TableHead><TableHead>Product</TableHead>
                  <TableHead>Rev</TableHead><TableHead>Stage</TableHead><TableHead>Approval</TableHead><TableHead></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {samples.map((s) => (
                    <TableRow key={s.id} className="hover:bg-secondary/40 cursor-pointer" onClick={() => setDetail(s.id)} data-testid={`sample-row-${s.sample_id}`}>
                      <TableCell className="font-num font-semibold text-primary">{s.sample_id}</TableCell>
                      <TableCell className="max-w-[120px] truncate">{s.buyer}</TableCell>
                      <TableCell className="max-w-[140px] truncate">{s.product_name}</TableCell>
                      <TableCell className="font-num">V{s.revision_number}</TableCell>
                      <TableCell><StatusBadge status={s.status} /></TableCell>
                      <TableCell><StatusBadge status={s.approval_status} /></TableCell>
                      <TableCell><div className="flex items-center gap-1 justify-end">
                        <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); setDetail(s.id); }} data-testid={`view-sample-${s.sample_id}`}><Eye className="w-4 h-4" /></Button>
                        {canWrite("samples") && <Button size="icon" variant="ghost" onClick={(e) => delSample(s.id, e)} data-testid={`delete-sample-${s.sample_id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>}
                      </div></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>

        <Card className="p-5">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-2">Sample Pipeline</h3>
          <div style={{ width: "100%", height: 260 }}>
            {pie.length ? (
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={pie} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={3}>
                    {pie.map((_, i) => <Cell key={i} fill={CHART[i % CHART.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 10, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <EmptyState title="No pipeline data" />}
          </div>
        </Card>
      </div>

      {newOpen && <SampleForm open onClose={() => setNewOpen(false)} products={products} customers={customers} sampleTypes={sampleTypes} printingTypes={printingTypes} />}
      {detail && <SampleDetail id={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}
